import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { brandShort } from "../context/CompanyContext";

/**
 * Floating WhatsApp button — draggable & always-on-top.
 *
 * Behaviour:
 *  - Floats above ALL UI (z-index 2147483600, near max int) so no modal,
 *    sticky header or overlay covers it.
 *  - Default position is bottom-right; user can drag to any corner.
 *  - The dragged position survives page reloads via localStorage.
 *  - A drag is ignored as a click (so we don't accidentally open WhatsApp
 *    while moving the button) — only "tap-without-drag" triggers the link.
 *  - Edge-snap: on release, the button hops to the nearest left/right
 *    edge so it never blocks middle-of-screen content.
 *  - Pointer Events API → works for mouse, touch, pen with one handler.
 */
const WHATSAPP_NUMBER = "351967386076"; // +351 967 386 076
const PREFILLED_MESSAGE =
  `Olá ${brandShort()}! Vim pela vossa página e gostaria de um orçamento para o meu apartamento.`;

const STORAGE_KEY = "luxstay.wafab.pos.v1";
const FAB_SIZE = 56; // px (w-14 h-14)
const MARGIN = 12;   // px from viewport edge
const DRAG_THRESHOLD = 4; // px of movement before we treat it as a drag

function defaultPosition() {
  if (typeof window === "undefined") return { x: 0, y: 0 };
  return {
    x: window.innerWidth - FAB_SIZE - 20,
    y: window.innerHeight - FAB_SIZE - 20,
  };
}

function clampToViewport(p) {
  if (typeof window === "undefined") return p;
  const maxX = window.innerWidth - FAB_SIZE - MARGIN;
  const maxY = window.innerHeight - FAB_SIZE - MARGIN;
  return {
    x: Math.max(MARGIN, Math.min(maxX, p.x)),
    y: Math.max(MARGIN, Math.min(maxY, p.y)),
  };
}

function loadStoredPosition() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (typeof obj?.x === "number" && typeof obj?.y === "number") {
      return clampToViewport(obj);
    }
  } catch { /* corrupt entry, ignore */ }
  return null;
}

export default function WhatsAppFab() {
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(PREFILLED_MESSAGE)}`;
  const [pos, setPos] = useState(() => loadStoredPosition() || defaultPosition());
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({
    pointerId: null,
    startX: 0, startY: 0,        // pointer position at down
    originX: 0, originY: 0,      // fab position at down
    moved: false,
  });
  const fabRef = useRef(null);

  // Re-clamp + re-default on viewport resize so the button never falls
  // off-screen after a window resize / device rotation.
  useEffect(() => {
    function onResize() {
      setPos(p => clampToViewport(p));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function onPointerDown(e) {
    if (!fabRef.current) return;
    fabRef.current.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX, startY: e.clientY,
      originX: pos.x, originY: pos.y,
      moved: false,
    };
    setDragging(true);
  }

  function onPointerMove(e) {
    if (dragRef.current.pointerId !== e.pointerId) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (!dragRef.current.moved &&
        Math.hypot(dx, dy) < DRAG_THRESHOLD) {
      return; // not yet a real drag
    }
    dragRef.current.moved = true;
    setPos(clampToViewport({
      x: dragRef.current.originX + dx,
      y: dragRef.current.originY + dy,
    }));
  }

  function onPointerUp(e) {
    if (dragRef.current.pointerId !== e.pointerId) return;
    fabRef.current?.releasePointerCapture?.(e.pointerId);
    setDragging(false);

    if (dragRef.current.moved) {
      // Snap to nearest horizontal edge for a polished, predictable feel.
      const half = window.innerWidth / 2;
      const snapped = clampToViewport({
        x: pos.x < half - FAB_SIZE / 2
            ? MARGIN
            : window.innerWidth - FAB_SIZE - MARGIN,
        y: pos.y,
      });
      setPos(snapped);
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapped));
      } catch { /* storage full or disabled — fine */ }
    }
    dragRef.current.pointerId = null;
  }

  function onClick(e) {
    // Suppress the click that fires after a drag on touch devices.
    if (dragRef.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current.moved = false;
    }
  }

  return (
    <a href={href}
       ref={fabRef}
       target="_blank"
       rel="noopener noreferrer"
       aria-label="Falar connosco pelo WhatsApp (arrastável)"
       onPointerDown={onPointerDown}
       onPointerMove={onPointerMove}
       onPointerUp={onPointerUp}
       onPointerCancel={onPointerUp}
       onClick={onClick}
       style={{
         left: `${pos.x}px`,
         top: `${pos.y}px`,
         touchAction: "none",
         zIndex: 2147483600,
         cursor: dragging ? "grabbing" : "grab",
       }}
       className="whatsapp-fab fixed inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-[0_8px_24px_rgba(37,211,102,0.45)] hover:scale-110 transition-[transform,box-shadow] focus:outline-none focus:ring-2 focus:ring-[#25D366]/60 focus:ring-offset-2 focus:ring-offset-[#041f1b] select-none"
       data-testid="whatsapp-fab">
      <MessageCircle size={26} strokeWidth={2} fill="currentColor" className="text-white pointer-events-none" />
      <span className="whatsapp-pulse absolute inset-0 rounded-full pointer-events-none" aria-hidden="true" />

      <style>{`
        @keyframes whatsappPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.55); }
          50%      { box-shadow: 0 0 0 14px rgba(37, 211, 102, 0); }
        }
        .whatsapp-fab { animation: whatsappPulse 2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .whatsapp-fab { animation: none; }
        }
      `}</style>
    </a>
  );
}
